import { Stack, Text } from '@/components/custom-components'
import { Loader } from '@/components/custom-components/Loader'
import { ErrorDisplay } from '@/components/error.tsx'
import { BrandsGridView } from '@/components/templates/BrandsGridView'
import { trpc } from '@/utils/trpc.ts'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'

type SearchParams = {
  sideBarOpen?: boolean
  searchTerm?: string
}

export const Route = createFileRoute('/feeds/search/brands')({
  component: All,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const sideBarOpen = search?.sideBarOpen as boolean
    const searchTerm = search?.searchTerm as string | undefined

    return {
      sideBarOpen,
      searchTerm,
    }
  },
})

function All() {
  const LIMIT = 20
  const { searchTerm: querySearchTerm } = Route.useSearch()
  const {
    data: brands,
    isLoading,
    refetch,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = trpc.searchBrands.useInfiniteQuery(
    {
      limit: LIMIT,
      query: querySearchTerm ?? '',
    },
    {
      getNextPageParam: (lastPage) => lastPage.cursor,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )

  const { ref: scrollRef, inView } = useInView({
    threshold: 0,
    trackVisibility: true,
    delay: 100,
    initialInView: false,
  })

  useEffect(() => {
    refetch()
  }, [querySearchTerm])

  useEffect(() => {
    if (
      inView &&
      hasNextPage &&
      !isFetchingNextPage &&
      brands &&
      brands.pages[brands.pages.length - 1].data.length >= LIMIT
    ) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, fetchNextPage, brands])

  const mappedBrands = useMemo(() => {
    return (
      brands?.pages.flatMap((page) => {
        return page.data.flatMap((b) => ({ ...b, fbPageId: b.brandId, brandImage: b.brandImage ?? null }))
      }) ?? []
    )
  }, [brands])

  if (error) {
    return <ErrorDisplay />
  }

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center w-full h-screen m-auto'}>
        <Loader />
      </div>
    )
  }

  return (
    <Stack className="gap-3 lg:gap-6">
      <Stack className="gap-3 lg:gap-6 lg:sticky lg:top-0 lg:z-10 lg:bg-themebackground dark:lg:bg-themebackgrounddark lg:py-2 flex justify-between items-center w-full">
        <div className={'flex justify-between gap-5 items-center w-full'}>
          <Text weight="semibold" size={'xxl'} className="w-fit">
            Your Search Results {brands ? ` (${brands?.pages[0]?.totalHits})` : ''}:
            <span className="opacity-45">“{querySearchTerm}”</span>
          </Text>
        </div>
      </Stack>

      {brands && mappedBrands && <BrandsGridView data={mappedBrands} />}
      <div ref={scrollRef} className="flex justify-center w-full min-h-14 h-14">
        {isFetchingNextPage && <Loader />}
      </div>
    </Stack>
  )
}
